import { useState, useCallback } from 'react';
import LoqateApi from 'services/api/thirdparty/Loqate';
import ReactDOM from 'react-dom';
import LanguageLocale from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import processEvent from 'analytics/processEvent';

const { getCurrentLanguage, LANGUAGES } = LanguageLocale;

const AVS_ELEMENTS_ON_VIEW = 6;

// AVS Loqate v2 (GIM-5453, PRD §3.2 Δ10): Loqate Find fires only after 3 alphanumeric characters
// have been typed into address line 1, cutting wasted paid lookups on short fragments. Gated behind
// avsLoqateV2 — with the flag off, the legacy behavior (fire on any length) is preserved.
const MIN_TYPEAHEAD_ALNUM_CHARS = 3;
// Unicode-aware so accented letters in French-Canadian street names (é, à, î, ç, …) count toward the
// gate the same as ASCII letters — otherwise fr-CA addresses would trigger the lookup a keystroke late.
const countAlphanumeric = text => (typeof text === 'string' ? (text.match(/[\p{L}\p{N}]/gu) || []).length : 0);

/**
 * Loqate address typeahead for the address forms. Queries Loqate Find/Retrieve, manages the
 * suggestion list and keyboard-highlighted index, drills into multi-unit (non-Address) results, and
 * — under AVS Loqate v2 (avsLoqateV2) — gates the lookup at >=3 alphanumeric characters.
 * Selecting an Address-type suggestion hands the caller the canonical address via
 * updateLoqateVerifiedAddressState.
 * @param {Object} params
 * @param {Object} params.address current address; address.country scopes the Loqate query
 * @param {Object} params.loqateWaitingForRef ref holding the latest typed text (drops stale responses)
 * @param {Object} params.addressPredictionRef ref to the suggestion-list DOM node (for scroll-into-view)
 * @param {Function} params.updateLoqateVerifiedAddressState called with the retrieved address fields on select
 * @param {Function} params.setFieldFocus focuses the address input when drilling into a multi-unit result
 * @param {Function} params.handleAddress1Blur blurs address line 1 after a final selection
 * @param {Object} params.addressInputRef ref to the address line 1 input
 * @param {string} params.section analytics section label ('deliver to' | 'gift card')
 * @returns {Object} suggestion state + handlers (getLoqateAddresses, handleLoqateAddressClick, getHighlight, setHighlightedAddrIndex, …)
 */
export function useLoqateAddresses({
    address,
    loqateWaitingForRef,
    addressPredictionRef,
    updateLoqateVerifiedAddressState,
    setFieldFocus,
    handleAddress1Blur,
    addressInputRef,
    section
}) {
    const [loqateAddresses, setLoqateAddresses] = useState([]);
    const [highlightedAddrIndex, setHighlightedAddrIndexState] = useState(-1);
    const [highlightTexts, setHighlightTextsState] = useState([]);

    const updateScrollAVS = useCallback(
        pos => {
            const avsContainer = ReactDOM.findDOMNode(addressPredictionRef.current);

            if (!avsContainer || !avsContainer.hasChildNodes()) {
                return;
            }

            if (pos !== undefined) {
                avsContainer.scrollTop = pos;
            } else {
                // Optional chaining guards against a race where highlightedAddrIndex outlives the
                // children array (e.g., results shrink before the scroll-into-view callback runs);
                // an undefined height propagates as NaN and all comparisons below short-circuit.
                const addressItemHeight = avsContainer.children[highlightedAddrIndex]?.offsetHeight;
                const padding = 8;
                const avsVisibleHeight = AVS_ELEMENTS_ON_VIEW * addressItemHeight;
                const currentAddressPos = highlightedAddrIndex * addressItemHeight + padding;
                const maxViewPos = avsContainer.scrollTop + avsVisibleHeight + padding;

                // is the selected address' lower point after the bottom visible area limit
                if (maxViewPos < currentAddressPos + addressItemHeight + padding) {
                    avsContainer.scrollTop = currentAddressPos + addressItemHeight - avsVisibleHeight;
                    // is the selected address' top point before the top visible area limit
                } else if (avsContainer.scrollTop + padding > currentAddressPos) {
                    avsContainer.scrollTop = currentAddressPos - padding;
                }
            }
        },
        [highlightedAddrIndex]
    );

    const setHighlightedAddrIndex = useCallback(
        (index, callUpdateAVS = false) => {
            setHighlightedAddrIndexState(index);

            if (callUpdateAVS) {
                updateScrollAVS();
            }
        },
        [setHighlightedAddrIndexState, updateScrollAVS]
    );

    const getLoqateAddresses = useCallback(
        (text, container) => {
            // GIM-5453: gate typed lookups at >=3 alphanumeric chars when AVS Loqate v2 is on. Drill-in
            // requests (container present) always proceed since the shopper already picked a multi-unit
            // result. Flag off -> legacy behavior (fire on any length).
            if (Sephora.configurationSettings?.avsLoqateV2 && !container && countAlphanumeric(text) < MIN_TYPEAHEAD_ALNUM_CHARS) {
                setLoqateAddresses([]);
                // Also reset the keyboard-highlighted index + highlight text so the address input
                // doesn't keep a dangling aria-activedescendant (e.g. avs_result0) pointing into the
                // now-empty list (FirstAddressField sets it whenever highlightedAddrIndex > -1).
                setHighlightedAddrIndex(-1);
                setHighlightTextsState([]);

                return;
            }

            LoqateApi.findAddresses(text, address.country, container)
                .then(data => {
                    if (!container && text !== loqateWaitingForRef.current) {
                        return;
                    }

                    if (data.Items && data.Items.length === 1 && typeof data.Items[0].Error !== 'undefined') {
                        setLoqateAddresses([]);
                    } else {
                        if (data.Items && data.Items.length > 0) {
                            setLoqateAddresses(data.Items);
                            setHighlightedAddrIndex(0, () => updateScrollAVS(0));
                        } else {
                            setLoqateAddresses([]);
                        }
                    }
                })
                .catch(err => Sephora.logger.error('[useLoqateAddresses] findAddresses failed', err));
        },
        [address.country, loqateWaitingForRef, setHighlightedAddrIndex, updateScrollAVS]
    );

    const verifyLoqateAddress = useCallback(
        id => {
            LoqateApi.retrieveAddress(id)
                .then(data => {
                    if (data.Items && data.Items.length === 1 && typeof data.Items[0].Error !== 'undefined') {
                        // @TODO: handle error
                    } else {
                        // Check if there were any items found
                        if (data.Items && data.Items.length > 0) {
                            const currentLanguage = getCurrentLanguage().toUpperCase() === LANGUAGES.EN ? LANGUAGES.ENG : LANGUAGES.FRE;
                            // Update Fields with Loqate data
                            const addressObj = data.Items.filter(addressItem => addressItem.Language === currentLanguage)[0] || data.Items[0];
                            let addressLine1 = '';
                            const addressLine2 = addressObj.SubBuilding || '';
                            const city = addressObj.City || '';
                            const postalCode = addressObj.PostalCode || '';
                            const stateLocal = addressObj.Province || '';
                            const isPOBoxAddress = !!addressObj.POBoxNumber;
                            const addressType = addressObj.Type;

                            if (addressObj.BuildingNumber && addressObj.Street) {
                                addressLine1 = `${addressObj.BuildingNumber} ${addressObj.Street}`;
                            } else if (addressObj.Line1) {
                                addressLine1 = addressObj.Line1;
                            }

                            updateLoqateVerifiedAddressState({
                                addressLine1,
                                addressLine2,
                                city,
                                postalCode,
                                stateLocal,
                                isPOBoxAddress,
                                addressType
                            });
                        }
                    }
                })
                .catch(err => Sephora.logger.error('[useLoqateAddresses] retrieveAddress failed', err));
        },
        [updateLoqateVerifiedAddressState]
    );

    const handleLoqateAddressClick = useCallback(
        loqateAddress => {
            /* If the Type is not 'Address' it means when can call the Loqate service again because
               there are more addresses under this address (multiple units) */
            if (loqateAddress.Type !== 'Address') {
                setHighlightTexts(loqateAddress);

                getLoqateAddresses(loqateAddress.Text, loqateAddress.Id);
                // return the focus back to the address1 field because we need to show more addresses
                setFieldFocus(addressInputRef.current);
            } else {
                verifyLoqateAddress(loqateAddress.Id);
                // Hide AVS to show autocomplete fields
                handleAddress1Blur();
            }

            fireSelectLoqateAddressAnalytics();
        },
        [setFieldFocus, handleAddress1Blur, verifyLoqateAddress, getLoqateAddresses]
    );

    const fireSelectLoqateAddressAnalytics = () => {
        const actionString = `${section}:${anaConsts.ACTION_INFO.SELECT_LOQATE_ADDRESS}`;
        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        const eventData = {
            data: {
                actionInfo: actionString,
                previousPage: mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName
            }
        };

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, eventData);
    };

    const setHighlightTexts = useCallback(loqateAddress => {
        const highlights = loqateAddress.Highlight.split(';').shift();
        const highlightTextsData = [];

        if (highlights) {
            highlights.split(',').forEach(highlight => {
                const limits = highlight.split('-');
                highlightTextsData.push(loqateAddress.Text.substring(limits[0], limits[1]));
            });

            setHighlightTextsState(highlightTextsData);
        }
    }, []);

    const getHighlight = useCallback(
        loqateAddress => {
            const highlightIndex = [];

            if (loqateAddress.Highlight.trim()) {
                return loqateAddress.Highlight;
            }

            highlightTexts.forEach(highlightText => {
                const regex = new RegExp(highlightText, 'g');
                let match;

                while ((match = regex.exec(loqateAddress.Text))) {
                    highlightIndex.push(`${regex.lastIndex - match[0].length}-${regex.lastIndex}`);
                }
            });

            return highlightIndex.join(',');
        },
        [highlightTexts]
    );

    return {
        loqateAddresses,
        highlightedAddrIndex,
        highlightTexts,
        getLoqateAddresses,
        setLoqateAddresses,
        setHighlightedAddrIndexState,
        handleLoqateAddressClick,
        getHighlight,
        setHighlightedAddrIndex
    };
}
